import React, { useEffect, useState } from "react";
import { Dashboard, ReactLoading } from "../Dashboard/TinManagement/import";
import { Button, Card, message, Popconfirm, Table } from "antd";
import Search from "./components/Search";
import {
  CloudDownloadOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import MappingConfigDrawer from "./components/MappingConfigDrawer";
import StatusChip from "./components/StatusChip";
import VersantTypeChip from "./components/VersantTypeChip";
import SkillChip from "./components/SkillChip";
import {
  getAllDepartmentDesignationMapping,
  getAllDepartments,
  getAllDesignations,
} from "../../services/departments/departments.service";
import useAxios from "../../hooks/useAxios";
import { checkPermission } from "../../utils/checkPermission";
import useRole from "../../hooks/useRole";
import locations from "../../utils/api/locations";

const VersantConfig = () => {
  const [isEdited, setIsEdited] = useState(null);
  const [load, setLoad] = useState(false);
  const [openConfigModal, setOpenConfigModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [mapping, setMapping] = useState([]);
  const [processes, setProcesses] = useState([]);
  const [locationsData, setLocationsData] = useState([]);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const api = useAxios();

  const userRole = useRole();
  const [permissions, setPermissions] = useState({
    canCreate: false,
    canEdit: false,
    canDelete: false,
  });

  const checkPermissions = async () => {
    try {
      const [create, edit, del] = await Promise.all([
        checkPermission(userRole, "process_based_versant_module", "create"),
        checkPermission(userRole, "process_based_versant_module", "edit"),
        checkPermission(userRole, "process_based_versant_module", "delete"),
      ]);
      setPermissions({
        canCreate: create,
        canEdit: edit,
        canDelete: del,
      });
    } catch (err) {
      console.error("Error checking permissions:", err);
    }
  };

  useEffect(() => {
    checkPermissions();
  }, [userRole]);

  const [rows, setRows] = useState([]);

  const toggleMappingModal = () => setOpenConfigModal(!openConfigModal);

  const columns = [
    {
      title: "ROLE",
      dataIndex: "role",
      key: "role",
      sorter: (a, b) => a?.role?.localeCompare(b?.role),
    },
    {
      title: "DEPARTMENT",
      dataIndex: "department",
      key: "department",
      sorter: (a, b) => a?.department?.localeCompare(b?.department),
    },
    {
      title: "PROCESS (CLIENT)",
      dataIndex: "process",
      key: "process",
      sorter: (a, b) => a?.process?.localeCompare(b?.process),
    },
    {
      title: "LOCATION",
      dataIndex: "location",
      key: "location",
      sorter: (a, b) => a?.location?.localeCompare(b?.location),
    },
    {
      title: "VERSANT TYPE",
      dataIndex: "versant_type",
      key: "versant_type",
      render: (value, row) => {
        return <VersantTypeChip label={value} />;
      },
    },
    {
      title: "SKILL THRESHOLD",
      dataIndex: "skill_threshold",
      key: "skill_threshold",
      render: (value, row) => {
        const { versant_type } = row;
        return <SkillChip label={versant_type} value={value} />;
      },
    },
    {
      title: "STATUS",
      dataIndex: "status",
      key: "status",
      render: (value, row) => {
        return <StatusChip status={value} />;
      },
    },
    {
      title: "ACTIONS",
      dataIndex: "action",
      key: "action",
      fixed: "right",
      render: (value, row) => {
        return (
          <div className="w-full flex gap-1">
            {permissions.canEdit && (
              <span className="flex align-items-center justify-center">
                <EditOutlined
                  className="!text-[#0b56ad] !text-[1.3rem]"
                  onClick={() => {
                    setIsEdited(row?.data);
                    toggleMappingModal();
                  }}
                />
              </span>
            )}
            {permissions.canDelete && (
              <span className="flex align-items-center justify-center ">
                <Popconfirm
                  title="Are you sure you want to delete the mapping?"
                  description="Are you sure you want to delete the mapping?"
                  onConfirm={() => deleteConfigurations(value)}
                  okText="Yes"
                  cancelText="No"
                  placement="left"
                  okButtonProps={{
                    className: "!bg-red-500 hover:!bg-red-600 !border-none",
                  }}
                >
                  <DeleteOutlined className="!text-red-500 !text-[1.3rem]" />
                </Popconfirm>
              </span>
            )}
          </div>
        );
      },
    },
  ];

  const getDepartments = async () => {
    try {
      const { data } = await getAllDepartments(api, "user");
      const { trfDepartment } = data;
      setDepartments(trfDepartment.filter((e) => e.status));
    } catch (err) {
      console.error(err);
    }
  };

  const getDesignations = async () => {
    try {
      const { data } = await getAllDesignations(api, "user");
      const { designation } = data || {};
      setDesignations(designation?.filter((e) => e.status));
    } catch (err) {
      console.error(err);
    }
  };

  const getDepartmentDesignationMapping = async () => {
    try {
      const { data } = await getAllDepartmentDesignationMapping(api, "user");
      const { data: mapping } = data || {};
      setMapping(mapping?.filter((e) => e.status));
    } catch (err) {
      console.error(err);
    }
  };

  const getProcesses = async () => {
    try {
      const res = await api.get("/processes", { tokenType: "user" });
      setProcesses(res.data.processes);
    } catch (err) {
      console.error(err);
    }
  };

  const getLocations = async () => {
    try {
      const data = await locations();
      setLocationsData(data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchConfigurations = async () => {
    try {
      const { data } = await api.get("/processBasedVersant", {
        tokenType: "user",
      });

      const { data: records } = data;
      const _rows = [];

      for (let row of records) {
        const locId = row?.location?._id || (typeof row?.location === "string" ? row.location : null) || row?.locationId;
        const locationName =
          (typeof row?.location === "string" ? row.location : null) ||
          row?.location?.locationName ||
          locationsData.find((l) => l._id === locId)?.locationName ||
          "-";
        _rows.push({
          role: row?.designation?.name,
          department: row?.department?.name,
          process: row?.process?.name || "-",
          location: locationName,
          versant_type: row?.versantType,
          skill_threshold: {
            voice: row?.voiceScore,
            written: row?.writtenScore,
          },
          status: row?.isActive,
          action: row?._id,
          data: row,
        });
      }
      setRows(_rows);
    } catch (err) {
      console.error(err);
    }
  };

  const updateConfigurationRows = (configurations, savedRecords = []) => {
    const getConfigurationKey = (configuration) =>
      [
        configuration?.designationId,
        configuration?.departmentId,
        configuration?.processId,
        configuration?.location,
      ]
        .map((value) => String(value || "").toLowerCase())
        .join("|");

    const getRowKey = (row) =>
      getConfigurationKey({
        designationId: row.data?.designation?._id,
        departmentId: row.data?.department?._id,
        processId: row.data?.process?._id,
        location:
          typeof row.data?.location === "string"
            ? row.data.location
            : row.location,
      });

    const uniqueUpdates = new Map();
    configurations.forEach((configuration, index) => {
      uniqueUpdates.set(getConfigurationKey(configuration), {
        configuration,
        savedRecord: savedRecords[index],
      });
    });

    const updatedRows = Array.from(uniqueUpdates.values()).map(
      ({ configuration, savedRecord }) => {
        const designation = designations.find(
          (item) => item?._id === configuration.designationId,
        );
        const department = departments.find(
          (item) => item?._id === configuration.departmentId,
        );
        const process = processes.find(
          (item) => item?._id === configuration.processId,
        );

        const updatedData = {
          ...savedRecord,
          location: configuration.location,
          versantType: configuration.versantType,
          voiceScore: configuration.voiceScore,
          writtenScore: configuration.writtenScore,
          isActive: configuration.isActive,
          designation: {
            _id: configuration.designationId,
            name: designation?.designation,
          },
          department: {
            _id: configuration.departmentId,
            name: department?.department || department?.departmentName,
          },
          process: {
            _id: configuration.processId,
            name: process?.name,
          },
        };

        return {
          role: designation?.designation || "-",
          department:
            department?.department || department?.departmentName || "-",
          process: process?.name || "-",
          location: configuration.location || "-",
          versant_type: configuration.versantType,
          skill_threshold: {
            voice: configuration.voiceScore,
            written: configuration.writtenScore,
          },
          status: configuration.isActive,
          action: savedRecord?._id,
          data: updatedData,
        };
      },
    );

    setRows((currentRows) => {
      const updatedKeys = new Set(uniqueUpdates.keys());
      const unchangedRows = currentRows.filter(
        (row) => !updatedKeys.has(getRowKey(row)),
      );

      return [...updatedRows, ...unchangedRows];
    });
    setCurrentPage(1);
  };

  const deleteConfigurations = async (_id) => {
    try {
      await api.delete(`/processBasedVersant/${_id}`, { tokenType: "user" });
      await fetchConfigurations();
      message.success("Configuration Delete Successfully.");
    } catch (err) {
      console.error(err);
      message.error(err?.response?.data?.message || "Something went wrong");
    }
  };

  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);
        await Promise.all([
          fetchConfigurations(),
          getDepartments(),
          getDesignations(),
          getDepartmentDesignationMapping(),
          getProcesses(),
          getLocations(),
        ]);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    init();
  }, []);

  const filteredRows = rows.filter((row) => {
    const _search = search.toLowerCase();

    return (
      row.role.toLowerCase().includes(_search) ||
      row.department.toLowerCase().includes(_search) ||
      row.process.toLowerCase().includes(_search) ||
      row.location.toLowerCase().includes(_search)
    );
  });

  return (
    <Dashboard title="Versant Setup">
      {loading && (
        <ReactLoading type="spin" color="#0000FF" className="spinnerBg" />
      )}
      <div className="py-1 px-1">
        <h4 className="text-[1.5rem] mb-0">Versant Configuration</h4>
        <p className="text-[0.89rem]">
          Centralized configuration for Versant assessment thresholds.
        </p>
      </div>
      <div>
        <Card>
          <div className="flex flex-col gap-[1.5rem]">
            <div className="flex justify-between">
              <Search search={search} setSearch={setSearch} />
              {permissions.canCreate && (
                <button
                  onClick={toggleMappingModal}
                  data-testid="add-new"
                  className="btn btn-primary flex gap-[10px] items-center"
                >
                  <PlusOutlined style={{ fontSize: "1.1rem", fontWeight: 600 }} />
                  <span className="flex items-center text-[16px] font-semibold">
                    Create Mapping
                  </span>
                </button>
              )}
            </div>
            <div>
              <Table
                columns={columns}
                dataSource={filteredRows}
                rowKey="action"
                pagination={{
                  current: currentPage,
                  total: filteredRows?.length,
                  pageSizeOptions: ["5", "10", "30"],
                  showSizeChanger: true,
                  onChange: (page) => setCurrentPage(page),
                }}
                showSorterTooltip={false}
                scroll={{ x: true }}
                style={{ whiteSpace: "pre" }}
                exportFileName="details"
                size="small"
              />
            </div>
          </div>
        </Card>
      </div>
      {openConfigModal && (
        <MappingConfigDrawer
          openConfigModal={openConfigModal}
          toggleMappingModal={toggleMappingModal}
          fetchConfigurations={fetchConfigurations}
          updateConfigurationRows={updateConfigurationRows}
          designations={designations}
          departments={departments}
          mapping={mapping}
          processes={processes}
          setLoad={setLoad}
          isEdited={isEdited}
          setIsEdited={setIsEdited}
        />
      )}
    </Dashboard>
  );
};

export default VersantConfig;
